import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import {
  AlertTriangle,
  AlertCircle,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Copy,
  CheckCircle,
  XCircle,
  Clock,
  Globe,
  User,
  Filter,
  Download,
  X
} from 'lucide-react';

const ErrorLogs = ({ superAdminToken }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [expandedLog, setExpandedLog] = useState(null);
  const [copiedRef, setCopiedRef] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    severity: '',
    reference_id: '',
    start_date: '',
    end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.reference_id) params.append('reference_id', filters.reference_id);
      if (filters.start_date) params.append('start_date', `${filters.start_date}T00:00:00Z`);
      if (filters.end_date) params.append('end_date', `${filters.end_date}T23:59:59Z`);
      params.append('limit', limit);
      params.append('offset', page * limit);
      
      const response = await api.get(`/super-admin/error-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${superAdminToken}`
        }
      });
      
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch error logs');
    } finally {
      setLoading(false);
    }
  }, [superAdminToken, filters, page, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedRef(text);
    setTimeout(() => setCopiedRef(null), 2000);
  };

  const getSeverityStyle = (severity) => {
    switch (severity) {
      case 'critical':
        return { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' };
      case 'error':
        return { backgroundColor: '#ffedd5', color: '#9a3412', borderColor: '#fed7aa' };
      case 'warning':
        return { backgroundColor: '#fef9c3', color: '#854d0e', borderColor: '#fef08a' };
      default:
        return { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#bfdbfe' };
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <XCircle size={14} />;
      case 'error':
        return <AlertTriangle size={14} />;
      case 'warning':
        return <AlertCircle size={14} />;
      default:
        return <CheckCircle size={14} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const totalPages = Math.ceil(total / limit);

  const styles = {
    container: {
      padding: '1.5rem'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#1e293b',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    headerActions: {
      display: 'flex',
      gap: '0.5rem'
    },
    btn: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      backgroundColor: '#f1f5f9',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontSize: '0.875rem',
      color: '#475569'
    },
    searchBox: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      backgroundColor: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      minWidth: '250px'
    },
    searchInput: {
      border: 'none',
      outline: 'none',
      flex: 1,
      fontSize: '0.875rem'
    },
    filtersPanel: {
      backgroundColor: 'white',
      borderRadius: '0.75rem',
      padding: '1rem',
      marginBottom: '1rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    filterLabel: {
      fontSize: '0.75rem',
      fontWeight: '500',
      color: '#64748b'
    },
    filterInput: {
      padding: '0.5rem',
      border: '1px solid #e2e8f0',
      borderRadius: '0.375rem',
      fontSize: '0.875rem'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      padding: '0.75rem 1rem',
      textAlign: 'left',
      backgroundColor: '#f8fafc',
      borderBottom: '1px solid #e2e8f0',
      fontSize: '0.75rem',
      fontWeight: '600',
      color: '#64748b',
      textTransform: 'uppercase'
    },
    td: {
      padding: '0.75rem 1rem',
      borderBottom: '1px solid #f1f5f9',
      fontSize: '0.875rem',
      color: '#334155',
      verticalAlign: 'top'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '500',
      border: '1px solid'
    },
    refId: {
      fontFamily: 'monospace',
      backgroundColor: '#f1f5f9',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      cursor: 'pointer'
    },
    expandBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0.25rem',
      color: '#6366f1'
    },
    stackTrace: {
      backgroundColor: '#1e293b',
      color: '#e2e8f0',
      padding: '1rem',
      borderRadius: '0.5rem',
      fontSize: '0.75rem',
      fontFamily: 'monospace',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      maxHeight: '300px',
      overflow: 'auto',
      marginTop: '0.5rem'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      borderTop: '1px solid #e2e8f0'
    },
    pageInfo: {
      fontSize: '0.875rem',
      color: '#64748b'
    },
    pageButtons: {
      display: 'flex',
      gap: '0.5rem'
    },
    pageBtn: {
      padding: '0.5rem 1rem',
      border: '1px solid #e2e8f0',
      borderRadius: '0.375rem',
      backgroundColor: 'white',
      cursor: 'pointer',
      fontSize: '0.875rem'
    },
    emptyState: {
      padding: '3rem',
      textAlign: 'center',
      color: '#64748b'
    },
    alert: {
      padding: '1rem',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      border: '1px solid #fecaca'
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
          <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading error logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <AlertTriangle size={24} style={{ color: '#dc2626' }} />
          Error Logs
        </h1>
        <div style={styles.headerActions}>
          <div style={styles.searchBox}>
            <Search size={16} style={{ color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search by Reference ID..."
              style={styles.searchInput}
              value={filters.reference_id}
              onChange={(e) => setFilters({ ...filters, reference_id: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
            />
          </div>
          <button 
            style={styles.btn} 
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filters
          </button>
          <button 
            style={styles.btn} 
            onClick={fetchLogs}
            disabled={loading}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div style={styles.alert}>
          <XCircle size={18} />
          {error}
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div style={styles.filtersPanel}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Severity</label>
            <select
              style={styles.filterInput}
              value={filters.severity}
              onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Start Date</label>
            <input
              type="date"
              style={styles.filterInput}
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>End Date</label>
            <input
              type="date"
              style={styles.filterInput}
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            />
          </div>
          <div style={{ ...styles.filterGroup, justifyContent: 'flex-end' }}>
            <button
              style={{ ...styles.btn, backgroundColor: '#6366f1', color: 'white', border: 'none' }}
              onClick={fetchLogs}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Logs Table */}
      <div style={styles.card}>
        {logs.length === 0 ? (
          <div style={styles.emptyState}>
            <CheckCircle size={48} style={{ color: '#10b981', marginBottom: '1rem' }} />
            <p style={{ fontWeight: '500', color: '#334155' }}>No error logs found</p>
            <p style={{ fontSize: '0.875rem' }}>No errors match your current filters.</p>
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Reference</th>
                  <th style={styles.th}>Severity</th>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Organization</th>
                  <th style={styles.th}>Error Type</th>
                  <th style={styles.th}>Endpoint</th>
                  <th style={styles.th}>Time</th>
                  <th style={styles.th}>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr>
                      <td style={styles.td}>
                        {log.reference_id ? (
                          <span
                            style={styles.refId}
                            onClick={() => copyToClipboard(log.reference_id)}
                            title="Click to copy"
                          >
                            {log.reference_id}
                            {copiedRef === log.reference_id ? (
                              <CheckCircle size={12} style={{ color: '#10b981' }} />
                            ) : (
                              <Copy size={12} />
                            )}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>-</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, ...getSeverityStyle(log.severity) }}>
                          {getSeverityIcon(log.severity)}
                          {log.severity || 'error'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <code style={{ fontSize: '0.75rem' }}>{log.error_type}</code>
                      </td>
                      <td style={styles.td}>
                        {log.user_name || log.user_email ? (
                          <div>
                            <p style={{ fontWeight: '500', fontSize: '0.8125rem' }}>{log.user_name || 'Unknown'}</p>
                            <p style={{ fontSize: '0.7rem', color: '#64748b' }}>{log.user_email || '-'}</p>
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '0.8125rem' }}>-</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        {log.team_name ? (
                          <span style={{ 
                            backgroundColor: '#f0fdf4', 
                            color: '#166534',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            fontWeight: '500'
                          }}>
                            {log.team_name}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '0.8125rem' }}>-</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <code style={{ fontSize: '0.75rem' }}>{log.error_type}</code>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: '0.75rem' }}>
                          <span style={{ 
                            backgroundColor: '#e0e7ff', 
                            color: '#4338ca',
                            padding: '0.125rem 0.375rem',
                            borderRadius: '0.25rem',
                            marginRight: '0.25rem'
                          }}>
                            {log.request_method}
                          </span>
                          {log.request_path?.substring(0, 30)}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem' }}>
                          <Clock size={12} style={{ color: '#94a3b8' }} />
                          {formatDate(log.created_at)}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <button
                          style={styles.expandBtn}
                          onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                        >
                          {expandedLog === log.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </td>
                    </tr>
                    {expandedLog === log.id && (
                      <tr>
                        <td colSpan="8" style={{ ...styles.td, backgroundColor: '#f8fafc' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                            <div>
                              <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Full Error Message</p>
                              <p style={{ fontSize: '0.875rem' }}>{log.error_message}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Request Info</p>
                              <p style={{ fontSize: '0.875rem' }}>
                                <Globe size={12} style={{ marginRight: '0.25rem' }} />
                                IP: {log.ip_address || '-'}
                              </p>
                              <p style={{ fontSize: '0.875rem' }}>
                                <User size={12} style={{ marginRight: '0.25rem' }} />
                                User ID: {log.user_id || '-'}
                              </p>
                            </div>
                          </div>
                          {log.stack_trace && (
                            <div>
                              <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Stack Trace</p>
                              <pre style={styles.stackTrace}>{log.stack_trace}</pre>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div style={styles.pagination}>
              <span style={styles.pageInfo}>
                Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total} errors
              </span>
              <div style={styles.pageButtons}>
                <button
                  style={{ ...styles.pageBtn, opacity: page === 0 ? 0.5 : 1 }}
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                >
                  Previous
                </button>
                <button
                  style={{ ...styles.pageBtn, opacity: page >= totalPages - 1 ? 0.5 : 1 }}
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ErrorLogs;
